import type { AnyExtension } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";

import type { EditorCapabilities } from "../types";
import { Alignment } from "./alignment";
import { DefaultCapabilities } from "./default";
import { File, type FileAttrs } from "./file";
import { Iframe } from "./iframe";
import { ResizableImage } from "./image";
import { createMentionExtension } from "./mention";
import { Video } from "./video";

export { DefaultCapabilities };

export type BuildExtensionsContext = {
  onFileClick?: ((src: string | null, attrs: FileAttrs) => void) | undefined;
  /** Render rich mention content (e.g. user chips) in read-only viewers. */
  hasMentionRenderer?: boolean;
  placeholder?: () => string;
};

export const buildExtensions = (
  capabilities: EditorCapabilities,
  context: BuildExtensionsContext,
): AnyExtension[] => {
  const listsEnabled = !!capabilities.bulletList || !!capabilities.orderedList;

  const extensions: AnyExtension[] = [
    StarterKit.configure({
      // The editor only ever offered the formatting below; anything else stays disabled so the
      // stored HTML can't grow nodes the sanitiser or the presets don't expect.
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      trailingNode: false,
      link: false,
      bold: capabilities.bold ? {} : false,
      italic: capabilities.italic ? {} : false,
      strike: capabilities.strike ? {} : false,
      underline: capabilities.underline ? {} : false,
      heading: capabilities.heading
        ? {
            levels: (capabilities.heading.levels ?? [1, 2]) as Array<
              1 | 2 | 3 | 4 | 5 | 6
            >,
          }
        : false,
      bulletList: capabilities.bulletList ? {} : false,
      orderedList: capabilities.orderedList ? {} : false,
      listItem: listsEnabled ? {} : false,
      listKeymap: listsEnabled ? {} : false,
    }),
    Placeholder.configure({ placeholder: () => context.placeholder?.() ?? "" }),
  ];

  if (capabilities.link) {
    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: capabilities.link.autoLink ?? true,
        linkOnPaste: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer nofollow",
        },
      }),
    );
  }

  if (capabilities.image) {
    extensions.push(
      ResizableImage.configure({
        inline: false,
        allowBase64: false,
        resize: {
          enabled: true,
          directions: ["left", "right"],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
    );
  }

  // Emoji suggestions ride along with the mention extension, so it is needed for either trigger.
  if (capabilities.mentions?.length || capabilities.emoji) {
    extensions.push(
      createMentionExtension(capabilities.mentions ?? [], {
        emojiChar: capabilities.emoji ? ":" : undefined,
        withNodeView: !!context.hasMentionRenderer,
      }),
    );
  }

  if (capabilities.alignment) {
    extensions.push(Alignment);
  }

  if (capabilities.file) {
    extensions.push(File.configure({ onClick: context.onFileClick }));
  }

  if (capabilities.video) {
    extensions.push(
      Video.configure({
        srcRegex: capabilities.video.srcRegex,
        resize: {
          enabled: true,
          minWidth: 322,
          minHeight: 172,
          preserveAspectRatio: true,
        },
      }),
    );
  }

  if (capabilities.iframe) {
    extensions.push(
      Iframe.configure({
        resize: {
          enabled: true,
          minWidth: 200,
          minHeight: 150,
          preserveAspectRatio: true,
        },
      }),
    );
  }

  return extensions;
};
